import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, ClipboardPlus, Loader2, RefreshCcw, Landmark } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

const AUBERGINE = "#5A4565";
const BERRY = "#6E1E3A";
const GREEN = "#2F4A2A";
const GOLD = "#C49247";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const TABS = [
  { value: "matched", label: "Matched - ready to load" },
  { value: "unmatched", label: "No customer match" },
  { value: "loaded", label: "Loaded orders" },
];

export default function AdminBankAuditQueue() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStatus = searchParams.get("status");
  const initialStatus = TABS.some((tab) => tab.value === requestedStatus) ? requestedStatus : "matched";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [status, setStatus] = useState(initialStatus);
  const [sort, setSort] = useState("date_desc");
  const [loading, setLoading] = useState(true);
  const [rematching, setRematching] = useState(false);

  const load = async (nextStatus = status, nextSort = sort) => {
    setLoading(true);
    try {
      const response = await api.get("/console/bank-audit/queue", { params: { status: nextStatus, sort: nextSort } });
      setRows(response.data.rows || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't load the bank queue.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = (value) => { setStatus(value); load(value, sort); };
  const changeSort = (value) => { setSort(value); load(status, value); };

  const rematch = async () => {
    setRematching(true);
    try {
      const response = await api.post("/console/bank-audit/queue/rematch");
      toast.success(`Rechecked ${response.data.updated || 0} bank row(s).`);
      await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Couldn't re-run matching."); }
    finally { setRematching(false); }
  };

  const loadOrder = (row) => {
    const query = new URLSearchParams({
      customer_id: row.matched_customer_id || "",
      name: row.matched_customer_name || "",
      email: row.matched_customer_email || "",
      phone: row.matched_customer_phone || "",
      address: row.matched_customer_address || "",
      bank_queue_id: row.id,
      bank_amount: String(row.amount || 0),
      bank_name: row.bank_name || "",
      bank_date: row.bank_date || "",
    });
    navigate(`/admin/orders/create?${query.toString()}`);
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="bank-auditor-queue-page" style={{ color: AUBERGINE }}>
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase font-semibold" style={{ color: BERRY }}>✦ Customer reconciliation</div>
          <h1 className="font-serif text-4xl mt-1">Bank Auditor Queue</h1>
          <p className="text-sm italic opacity-70 mt-1">Review matched payments, then load the confirmed order into the customer record.</p>
        </div>
        <button type="button" onClick={rematch} disabled={rematching} data-testid="bank-queue-rematch-button"
          className="h-10 px-4 inline-flex items-center justify-center gap-2 rounded text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: BERRY }}>
          {rematching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Re-run matching
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Summary label="Ready to load" value={summary.matched || 0} Icon={CheckCircle2} color={GREEN} testid="bank-queue-summary-matched" />
        <Summary label="No customer match" value={summary.unmatched || 0} Icon={AlertCircle} color="#A33030" testid="bank-queue-summary-unmatched" />
        <Summary label="Loaded orders" value={summary.loaded || 0} Icon={ClipboardPlus} color={GOLD} testid="bank-queue-summary-loaded" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="bank-queue-status-tabs">
          {TABS.map((tab) => (
            <button key={tab.value} type="button" onClick={() => changeStatus(tab.value)} data-testid={`bank-queue-tab-${tab.value}`}
              className="h-9 px-4 rounded-full border-2 text-xs font-semibold transition-colors"
              style={status === tab.value ? { backgroundColor: AUBERGINE, borderColor: AUBERGINE, color: "#fff" } : { backgroundColor: "#fff", borderColor: EDGE, color: AUBERGINE }}>
              {tab.label} ({summary[tab.value] || 0})
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold" data-testid="bank-queue-sort-control">
          Sort
          <select value={sort} onChange={(event) => changeSort(event.target.value)} data-testid="bank-queue-sort-select"
            className="h-9 px-3 border rounded bg-white" style={{ borderColor: EDGE, color: AUBERGINE }}>
            <option value="date_desc">Newest bank date</option>
            <option value="date_asc">Oldest bank date</option>
            <option value="amount_desc">Highest amount</option>
            <option value="amount_asc">Lowest amount</option>
            <option value="name_asc">Potential name A-Z</option>
          </select>
        </label>
      </div>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: EDGE }}>
        {loading ? (
          <div className="p-12 text-center" data-testid="bank-queue-loading"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center italic opacity-60" data-testid="bank-queue-empty">Nothing in this list yet.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: EDGE }} data-testid="bank-queue-list">
            {rows.map((row) => <QueueRow key={row.id} row={row} onLoad={() => loadOrder(row)} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

function QueueRow({ row, onLoad }) {
  const matched = row.match_status === "matched";
  const loaded = row.match_status === "loaded";
  return (
    <li className="p-4 sm:p-5" data-testid={`bank-queue-row-${row.id}`}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[120px_minmax(0,1fr)_160px_auto] lg:items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] opacity-55">Bank date</div>
          <div className="font-semibold mt-1" data-testid={`bank-queue-date-${row.id}`}>{row.bank_date}</div>
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] opacity-55">{matched || loaded ? "Matched customer" : "Potential name / bank reference"}</div>
          <div className="font-serif text-lg mt-1 break-words" data-testid={`bank-queue-name-${row.id}`}>
            {matched || loaded ? row.matched_customer_name : row.bank_name}
          </div>
          {(matched || loaded) && <div className="text-xs opacity-65 mt-0.5">{row.matched_customer_email}{row.match_reason ? ` · matched by ${row.match_reason}` : ""}</div>}
          {!matched && !loaded && row.candidates?.length > 0 && (
            <div className="text-xs mt-1" style={{ color: GOLD }} data-testid={`bank-queue-candidates-${row.id}`}>
              Possible but not certain: {row.candidates.map((candidate) => candidate.name).join(", ")}
            </div>
          )}
          <div className="text-xs opacity-55 mt-1 break-words">Bank text: {row.bank_name}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] opacity-55">Order amount</div>
          <div className="font-serif text-2xl mt-1" style={{ color: GREEN }} data-testid={`bank-queue-amount-${row.id}`}>{money(row.amount)}</div>
        </div>
        <div className="lg:text-right">
          {matched && (
            <button type="button" onClick={onLoad} data-testid={`bank-queue-load-order-${row.id}`}
              className="h-10 px-4 rounded text-white text-sm font-semibold inline-flex items-center gap-2" style={{ backgroundColor: GREEN }}>
              <ClipboardPlus className="w-4 h-4" /> Load order
            </button>
          )}
          {loaded && (
            <a href={`/admin/order/${row.loaded_order_id}`} data-testid={`bank-queue-open-order-${row.id}`}
              className="h-10 px-4 rounded border text-sm font-semibold inline-flex items-center gap-2" style={{ borderColor: GOLD, color: "#7A5A1E", backgroundColor: CREAM }}>
              <Landmark className="w-4 h-4" /> Open order
            </a>
          )}
          {!matched && !loaded && <span className="text-xs font-semibold text-red-700" data-testid={`bank-queue-no-match-${row.id}`}>No customer match</span>}
        </div>
      </div>
    </li>
  );
}

function Summary({ label, value, Icon, color, testid }) {
  return (
    <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-semibold opacity-65"><Icon className="w-4 h-4" style={{ color }} /> {label}</div>
      <div className="font-serif text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}